import { useEffect, useMemo, useState } from "react";
import usersApi from "../../api/usersApi";
import { Button } from "../Button";
import { Edit2, Trash2, Eye, Edit } from "lucide-react";
import { EmployeeEditForm } from "./EmployeeEditForm";
import { toast } from "react-toastify";
import { confirmSubmit } from "../../utils/confirmSubmit";
import accountApi from "../../api/accountApi";
import { useSelector } from "react-redux";

export const UserManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  const [isEditing, setIsEditing] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const token = useSelector((state) => state.auth.access_token);

  const [accountQuery, setAccountQuery] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    if (accountQuery.length < 3) {
      setAccounts([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoadingAccounts(true);
        const res = await accountApi.searchAccounts(accountQuery, token);
        setAccounts(res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingAccounts(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [accountQuery]);

  const fetchUsers = async () => {
    try {
      const response = await usersApi.getAllUsers(token);
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching users", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddClick = () => {
    setSelectedEmployee(null);
    setIsEditing(true);
    setIsReadOnly(false);
    setActiveTab("profile");
  };

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEditing(true);
    setIsReadOnly(false);
    setActiveTab("profile");
  };

  const handleViewClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEditing(false);
    setIsReadOnly(true);
    setActiveTab("profile");
  };

  const handleSaveEmployee = async (formData) => {
    try {
      if (selectedEmployee) {
        await usersApi.updateUser(selectedEmployee.id, formData, token);
      } else {
        await usersApi.addUser(formData, token);
      }
      fetchUsers();
      setActiveTab("list");
      setIsEditing(false);
    } catch (error) {
      console.log("Failed to save. Please try again.");
    }
  };

  const handlePasswordUpdate = async (passData) => {
    try {
      await usersApi.updateUserPassword(passData, token);
      toast.success("Password updated successfully!");
      fetchUsers();
      setActiveTab("list");
      setIsEditing(false);
    } catch (error) {
      console.log("error :", error);
      toast.error("Failed to update password.");
    }
  };

  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch =
      employee?.fullName?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
      employee?.email?.toLowerCase()?.includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "" || employee.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleColor = (role) => {
    switch (role) {
      case "ADMIN":
        return "bg-purple-100 text-purple-800";
      case "CUSTOMER":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // asc | desc
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const getValueByPath = (obj, path) =>
    path.split(".").reduce((acc, key) => acc?.[key], obj);

  const sortedTasks = useMemo(() => {
    if (!sortConfig.key) return filteredEmployees;

    return [...filteredEmployees].sort((a, b) => {
      const aVal = getValueByPath(a, sortConfig.key);
      const bVal = getValueByPath(b, sortConfig.key);

      if (!aVal) return 1;
      if (!bVal) return -1;

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === "asc"
        ? aVal > bVal
          ? 1
          : -1
        : aVal < bVal
          ? 1
          : -1;
    });
  }, [filteredEmployees, sortConfig]);

  const SortIcon = ({ column }) => {
    const isActive = sortConfig.key === column;

    return (
      <span className="inline-flex flex-col ml-1 text-[9px] leading-none">
        <span
          className={
            isActive && sortConfig.direction === "asc"
              ? "text-gray-900"
              : "text-gray-300"
          }
        >
          ▲
        </span>
        <span
          className={
            isActive && sortConfig.direction === "desc"
              ? "text-gray-900"
              : "text-gray-300"
          }
        >
          ▼
        </span>
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <div className="flex space-x-3">
            <Button label="Add Employee" onClick={handleAddClick} />
          </div>
        </div>

        <div className="space-y-2">
          <div className="">
            <div className="p-2 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Employee Directory
              </h3>
              <div className="flex space-x-2">
                <input
                  type="text"
                  name="my-unique-search-field"
                  autoComplete="one-time-code"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border rounded-lg px-4 py-1"
                  placeholder="Search..."
                />
                <select
                  className="border rounded-lg px-4 py-1"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="">All Roles</option>
                  <option value="ADMIN">Admin</option>
                  <option value="CUSTOMER">Customer</option>
                  <option value="USER">User</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">
                      #
                    </th>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer"
                      onClick={() => handleSort("fullName")}
                    >
                      <span className="flex flex-row items-center gap-1">
                        Employee <SortIcon column="fullName" />
                      </span>
                    </th>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer"
                      onClick={() => handleSort("mobileNumber")}
                    >
                      <span className="flex flex-row items-center gap-1">
                        Phone Number <SortIcon column="mobileNumber" />
                      </span>
                    </th>
                    <th
                      className="px-2 py-2 text-left text-xs font-medium text-gray-500 cursor-pointer"
                      onClick={() => handleSort("role")}
                    >
                      <span className="flex flex-row items-center gap-1">
                        Role <SortIcon column="role" />
                      </span>
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedTasks.map((emp, index) => (
                    <tr key={emp.id}>
                      <td className="px-2 py-1">{index + 1}</td>
                      <td className="px-2 py-1">
                        <div className="flex items-center">
                          <div className="h-7 w-7 bg-blue-500 rounded-full flex items-center text-sm uppercase justify-center text-white font-bold">
                            {emp.fullName?.[0]}
                          </div>
                          <div className="ml-3 font-medium text-gray-900">
                            {emp.fullName}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold text-start`}
                        >
                          {emp.mobileNumber}
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        <span
                          className={`px-2 py-1 rounded-full text-[10px] font-semibold ${getRoleColor(emp.role)}`}
                        >
                          {emp.role}
                        </span>
                      </td>
                      <td className="px-2 py-1 space-x-1 text-center">
                        <button
                          type="button"
                          onClick={() => handleViewClick(emp)}
                          className="text-blue-600"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditClick(emp)}
                          className="text-blue-600"
                        >
                          <Edit size={18} />
                        </button>

                        <button
                          type="button"
                          className="text-red-600"
                          onClick={async () => {
                            const isConfirmed = await confirmSubmit({
                              title: "Delete User",
                              text: "Are you sure you want to delete this user?",
                              confirmText: "Delete",
                              cancelText: "Cancel",
                            });

                            if (!isConfirmed) return;

                            usersApi
                              .deleteUser(emp.id, token)
                              .then(fetchUsers)
                              .catch((err) => {
                                console.error(err);
                                alert("Failed to delete user");
                              });
                          }}
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {activeTab === "profile" && (
        <div className="fixed -top-6 left-0 inset-0  bg-black/30 flex items-center justify-center">
          <EmployeeEditForm
            employee={selectedEmployee}
            isReadOnly={isReadOnly}
            onSave={handleSaveEmployee}
            onUpdatePassword={handlePasswordUpdate}
            accounts={accounts}
            loadingAccounts={loadingAccounts}
            selectedAccount={selectedAccount}
            setAccountQuery={setAccountQuery}
            setSelectedAccount={setSelectedAccount}
            setAccounts={setAccounts}
            accountQuery={accountQuery}
            onCancel={() => {
              setSelectedAccount(null);
              setAccountQuery("");
              setAccounts([]);
              setActiveTab("list");
              setIsEditing(false);
            }}
          />
        </div>
      )}
    </div>
  );
};
